
import { Card, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import React, {JSX} from 'react';
import FinancingEditor from './FinancingEditor/FinancingEditor';
import FinanceVehicleSaleEditor from './FinanceVehicleSaleEditor/FinanceVehicleSaleEditor';
import styles from './_finance-quote-editor.module.scss';
import FinanceDeferredDownEditor from "../FinanceDeferredDownEditor/FinanceDeferredDownEditor";
import FinanceLoanClassSelector from "../FinanceLoanClassSelector/FinanceLoanClassSelector";
import FinanceProfitCalculator from "../FinanceProfitCalculator/FinanceProfitCalculator";
import {Typography} from '@mui/material';
import classNames from "classnames";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import {IRuleViolation} from "../../../../Interfaces/Application/IApplicationQuote";
import {IConfiguration} from "../../../../Data/ConfigurationData";
import {ILookupItems} from "../../../../Interfaces/ILookupItems";
import {FinanceQuoteValues} from "../../../../utils/LoanCalcUtils";
import {TabName} from "../../../Views/OrderEditor/OrderEditorTabs";
import {getQuoteBackgroundColor} from "../../../../utils/quoteUtils";
import {VehicleButtonPanel, VehicleInfoComponent} from "../../../Views/OrderEditor/VehicleCard/VehicleInfoCard";
import FinanceIrregularPaymentEditor from "../FinanceIrregularPaymentStreamEditor/FinanceIrregularPaymentStreamEditor";
import FinancePaymentStreamDisplay from '../FinanceIrregularPaymentStreamEditor/FinancePaymentStreamDisplay/FinancePaymentStreamDisplay';
import {
  ApplicationDownPaymentResponse,
  FinanceLoanClass,
  FinanceQuote,
  FinanceQuoteNonUniformPaymentOption,
  FinanceWarranty,
  Vehicle
} from "@amsanalytics/react-shared-library";
import {IQuotePayType} from "../../../../Interfaces/Common/IQuotePayType";
import {IEmployee} from "../../../../Interfaces/Admin/IEmployee";

export interface FinanceQuoteEditorProps {
  financeLoanClasses: FinanceLoanClass[];
  selectedFinanceQuote: FinanceQuote;
  selectedFinanceLoanClass: FinanceLoanClass | undefined;
  onChangeSingleField: <K extends keyof FinanceQuote>(fieldName: K, value: FinanceQuote[K], applicationQuoteId?: string) => void;
  currentApp: IApplication;
  applicationFinanceErrors: IRuleViolation[];
  configuration: IConfiguration;
  lookups: ILookupItems;
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  financeQuoteValues: FinanceQuoteValues;
  onChangeSelectedQuote: (selectedFinanceQuoteId: string) => void;
  showProfitCalculator: boolean;
  navigate: (tabName: TabName) => void;
  financeQuoteIsDirty: boolean;
  openDialog: (options: { children: React.ReactNode }) => void;
  closeDialog: () => void;
  onUpdateApp: (updatedApp: IApplication) => void;
  onLoanClassChange: (loanClass: FinanceLoanClass) => void;
  financeWarranties: FinanceWarranty[];
  onWarrantyChange: (warranty: FinanceWarranty | null) => void;
  // Additional state for child components
  employees: IEmployee[];
  quotePayTypes: IQuotePayType[];
  onSolve: (newVehicle: Vehicle | undefined) => void;
  applicationDownPaymentResponses: ApplicationDownPaymentResponse[];
}

export default function FinanceQuoteEditor(props: FinanceQuoteEditorProps) {
  const quoteBackgroundColor = getQuoteBackgroundColor(props.financeQuoteIsDirty);

  const getNonUniformPaymentComponent = (nonUniformPaymentOption: FinanceQuoteNonUniformPaymentOption | undefined): JSX.Element => {
    switch (nonUniformPaymentOption) {
      case FinanceQuoteNonUniformPaymentOption.DeferredDown:
        return <FinanceDeferredDownEditor {...props} className={classNames(styles.quoteItemCard, styles.deferredDownHeader)} />;
      default:
        return <div></div>;
    }
  };
  
  const usingIrregularPayments = props.selectedFinanceQuote.nonUniformPaymentOption === FinanceQuoteNonUniformPaymentOption.IrregularPayments;
  
  return (
    <div className={styles.financeQuoteEditorContainer}>
      {/* Left Column */}
      <div className={styles.quoteEditorColumn}>
        <Card className={styles.quoteItemCard}>
          <CardHeader>
            <CardTitle>
              Loan Class
            </CardTitle>
          </CardHeader>
          <FinanceLoanClassSelector
            financeLoanClasses={props.financeLoanClasses}
            financeQuoteFieldIsDisabled={props.financeQuoteFieldIsDisabled}
            onLoanClassChange={props.onLoanClassChange}
            onChangeSingleField={props.onChangeSingleField}
            financeCompanies={props.lookups.financeCompanies}
            selectedFinanceQuote={props.selectedFinanceQuote}
            financeWarranties={props.financeWarranties}
            onWarrantyChange={props.onWarrantyChange}
          />
        </Card>
        <Card style={{ margin: '5px' }}>
          <CardHeader>
            <div className={styles.tileHeader}>
              <Typography>Vehicle</Typography>
              <div className={styles.buttonGroup}>
                <VehicleButtonPanel {...props} />
              </div>
            </div>
          </CardHeader>
          <VehicleInfoComponent {...props} hideCardHeader={true} />
        </Card>
        {/* Profit Calculator - hidden per Story #108 (Washout Screen Implementation).
            Replaced by inline Washout Report tab. Preserved for potential restoration.
        <Card className={styles.quoteItemCard}>
          <CardHeader>
            <CardTitle>
              Profit Calculator
            </CardTitle>
          </CardHeader>
          <FinanceProfitCalculator {...props} />
        </Card>
        */}
      </div>
      {/* Center Column */}
      <div className={styles.quoteEditorColumn}>
        <Card className={styles.quoteItemCard}>
          <CardHeader>
            <CardTitle>
              Vehicle Sale
            </CardTitle>
          </CardHeader>
          <FinanceVehicleSaleEditor {...props} />
        </Card>
        {/* <Card className={styles.quoteItemCard}>
          <CardHeader>
            <CardTitle>
              <div>
                <span>Deferred Down</span>
                {!((quoteWasCreatedAfterCutoverDate && deferredPayments.length > 0) || props.financeQuoteFieldIsDisabled("deferredPayments") || deferredPayments.length >= maxNumberOfPayments) && (
                  <Tooltip anchorElement="target" position="top" parentTitle={true}>
                    <IconButton
                      title={'Add Deferred Payment'}
                      style={{color: '#757575'}}
                      onClick={() => addDeferredPayment()}
                    >
                      <AddIcon style={{fontSize: '30px'}} />
                    </IconButton>
                  </Tooltip>
                )}
                
              </div>
            </CardTitle>
          </CardHeader> */}
        {getNonUniformPaymentComponent(props.selectedFinanceQuote.nonUniformPaymentOption)}
        {/* </Card> */}

      </div>
    
      {/* Right Column  */}
      <div className={styles.quoteEditorColumn}>
        <Card className={classNames(styles.quoteItemCard, styles.financingHeader)}>
          <CardHeader>
            <CardTitle>
              Financing
            </CardTitle>
          </CardHeader>
          <FinancingEditor {...props} />
        </Card>
      </div>
      
      <div className={styles.fullWidthSection}>
        {/* Full-width Irregular Payment Section */}
        {usingIrregularPayments && 
            <FinanceIrregularPaymentEditor
              selectedFinanceQuote={props.selectedFinanceQuote}
              financeQuoteFieldIsDisabled={props.financeQuoteFieldIsDisabled}
              lookups={props.lookups}
              currentApp={props.currentApp}
              openDialog={props.openDialog}
              closeDialog={props.closeDialog}
              onUpdateApp={props.onUpdateApp}
              className={classNames(styles.quoteItemCard, styles.irregularPaymentHeader)}
            />
        }
        <FinancePaymentStreamDisplay {...props} className={classNames(styles.quoteItemCard, styles.irregularPaymentHeader)} />
      </div>
    </div>
  );
}

